/**
 * Aktive Scanumgebung wählen — wirkt auf Pipeline-Trigger, Discovery,
 * Klassifikator. Default-Umgebung wenn nichts gewählt.
 */
import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Star } from "lucide-react";
import { scanEnvironmentsApi } from "@/api/endpoints";
import { useAuthStore } from "@/auth/store";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function EnvironmentSwitcher() {
  const qc = useQueryClient();
  const activeId = useAuthStore((s) => s.activeScanEnvironmentId);
  const setActive = useAuthStore((s) => s.setActiveScanEnvironment);

  const { data: envs } = useQuery({
    queryKey: ["scan-environments"],
    queryFn: scanEnvironmentsApi.list,
  });

  // Effective ID = explizit gewählt ODER Default
  const effective = useMemo(() => {
    if (!envs || envs.length === 0) return null;
    if (activeId != null && envs.some((e) => e.id === activeId)) return activeId;
    return envs.find((e) => e.is_default)?.id ?? envs[0]?.id ?? null;
  }, [envs, activeId]);

  // Wenn die aktive ID nicht mehr existiert: zurücksetzen
  useEffect(() => {
    if (activeId != null && envs && !envs.some((e) => e.id === activeId)) {
      setActive(null);
    }
  }, [activeId, envs, setActive]);

  if (!envs || envs.length <= 1) {
    return (
      <div className="text-xs text-muted-foreground hidden md:block">
        {envs?.[0]?.name ?? "Keine Umgebung"}
      </div>
    );
  }

  return (
    <Select
      value={effective?.toString()}
      onValueChange={(v) => {
        setActive(Number(v));
        qc.invalidateQueries({ queryKey: ["stats"] });
      }}
    >
      <SelectTrigger className="w-36 sm:w-56 h-8 min-w-0">
        <SelectValue placeholder="Scanumgebung wählen" />
      </SelectTrigger>
      <SelectContent>
        {envs.map((e) => (
          <SelectItem key={e.id} value={e.id.toString()}>
            <span className="inline-flex items-center gap-1.5">
              {e.is_default && <Star className="h-3 w-3 text-yellow-500" />}
              {e.is_locked && <Lock className="h-3 w-3 text-muted-foreground" />}
              {e.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Liefert die effektiv aktive Umgebung (User-Wahl > Default > null). */
export function useActiveScanEnvironment() {
  const activeId = useAuthStore((s) => s.activeScanEnvironmentId);
  const { data: envs } = useQuery({
    queryKey: ["scan-environments"],
    queryFn: scanEnvironmentsApi.list,
  });
  if (!envs || envs.length === 0) return null;
  if (activeId != null) {
    const match = envs.find((e) => e.id === activeId);
    if (match) return match;
  }
  return envs.find((e) => e.is_default) ?? envs[0] ?? null;
}
